import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, 
  LogIn, 
  LogOut, 
  Link, 
  Cloud, 
  Smartphone, 
  RotateCcw,
  X,
  Settings,
  Palette,
  Type,
  Zap
} from 'lucide-react';
import { useGame } from '../context/GameContext';
import { useAuth } from '../hooks/useAuth';
import { isUserAdmin } from '../utils/admin';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose }) => {
  const { gameState, actions } = useGame();
  const { user, signInWithGoogle, signOut, linkWithGoogle, isAnonymous, isAuthenticated } = useAuth();
  const isAdmin = isUserAdmin(user);

  const fontSizeOptions = [
    { size: 0.7, label: 'Small' },
    { size: 0.85, label: 'Medium' },
    { size: 1.0, label: 'Default' },
    { size: 1.1, label: 'Large' },
    { size: 1.2, label: 'X-Large' },
  ];

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
    }
  };

  const handleLinkAccount = async () => {
    try {
      await linkWithGoogle();
    } catch (error) {
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
    }
  };

  const handleResetGame = () => {
    actions.resetGame();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex flex-col overflow-hidden">

      <div className="min-h-full max-h-full flex flex-col relative z-10">
        {/* Header */}
        <motion.div 
          className="flex-shrink-0 bg-white border-b border-gray-200"
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          <div className="flex items-center justify-between p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Settings className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Settings
                </h1>
                <p className="text-sm text-gray-600">Customize your experience</p>
              </div>
            </div>
            <motion.button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <X size={24} />
            </motion.button>
          </div>
        </motion.div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden bg-gray-50">
          <div className="p-4 sm:p-6 pb-8">
            <div className="max-w-2xl mx-auto space-y-6">
              {/* Account Section */}
              <motion.div 
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
              >
                
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <User className="w-5 h-5 text-gray-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">Your Account</h3>
                  </div>
                  
                  {isAuthenticated ? (
                    <div className="space-y-6">
                      {/* User Profile */}
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                        <div className="flex items-center gap-4">
                          {user?.photoURL ? (
                            <img
                              src={user.photoURL}
                              alt="Profile"
                              className="w-12 h-12 rounded-full border-2 border-gray-200"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                              <User size={20} className="text-gray-600" />
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">
                              {user?.displayName || 'Anonymous Player'}
                            </p>
                            <div className="flex items-center gap-2 text-gray-600 mt-1">
                              {isAnonymous ? (
                                <>
                                  <Smartphone size={16} />
                                  <span className="text-sm">Local storage only</span>
                                </>
                              ) : (
                                <>
                                  <Cloud size={16} />
                                  <span className="text-sm">Synced across devices</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Account Actions */}
                      <div className="space-y-3 mt-4">
                        {isAnonymous && (
                          <motion.button
                            onClick={handleLinkAccount}
                            className="w-full flex items-center gap-4 p-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all"
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.99 }}
                          >
                            <div className="p-2 bg-blue-500 rounded-lg">
                              <Link size={20} />
                            </div>
                            <div className="text-left flex-1">
                              <p className="font-semibold">Link Google Account</p>
                              <p className="text-blue-100 text-sm">Sync your progress everywhere</p>
                            </div>
                          </motion.button>
                        )}

                        <motion.button
                          onClick={handleSignOut}
                          className="w-full flex items-center gap-4 p-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all border border-gray-200"
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                        >
                          <div className="p-2 bg-gray-200 rounded-lg">
                            <LogOut size={20} />
                          </div>
                          <span className="font-semibold">Sign Out</span>
                        </motion.button>
                      </div>
                    </div>
                  ) : (
                    <motion.button
                      onClick={handleGoogleSignIn}
                      className="w-full flex items-center gap-4 p-6 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all"
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <div className="p-3 bg-blue-500 rounded-lg">
                        <LogIn size={24} />
                      </div>
                      <div className="text-left flex-1">
                        <p className="font-bold text-lg">Sign in with Google</p>
                        <p className="text-blue-100 text-sm">Join the Pathways experience</p>
                      </div>
                    </motion.button>
                  )}
                </div>
              </motion.div>

              {/* Game Settings */}
              <motion.div 
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <Type className="w-5 h-5 text-gray-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">Game Settings</h3>
                </div>

                <div className="space-y-6">
                  {/* Font Size */}
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <Palette className="w-4 h-4 text-gray-600" />
                      <label className="font-medium text-gray-700">Text Size</label>
                    </div>
                    <div className="grid grid-cols-5 gap-2">
                      {fontSizeOptions.map((option, index) => (
                        <motion.button
                          key={option.size}
                          onClick={() => actions.setFontSize(option.size)}
                          className={`p-3 rounded-lg font-semibold transition-all ${
                            gameState.fontSizeScale === option.size
                              ? 'bg-gray-900 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 + index * 0.05 }}
                        >
                          <div className="text-xs mb-1">A</div>
                          <div className="text-xs opacity-75">{option.label}</div>
                        </motion.button>
                      ))}
                    </div>
                  </div>

                  {/* Confirm Mode */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-200 rounded-lg">
                          <Zap className="w-4 h-4 text-gray-600" />
                        </div>
                        <div>
                          <label className="font-medium text-gray-900">Confirm Connections</label>
                          <p className="text-sm text-gray-600">Ask before making connections</p>
                        </div>
                      </div>
                      <motion.button
                        onClick={() => actions.setConfirmMode(!gameState.confirmMode)}
                        className={`relative w-12 h-6 rounded-full transition-all ${
                          gameState.confirmMode ? 'bg-gray-900' : 'bg-gray-300'
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <motion.div
                          className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm"
                          animate={{ x: gameState.confirmMode ? 24 : 0 }}
                          transition={{ type: "spring", stiffness: 400, damping: 30 }}
                        />
                      </motion.button>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Game Actions - Admin Only */}
              {isAdmin && (
                <motion.div 
                  className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <RotateCcw className="w-5 h-5 text-red-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">Admin Actions</h3>
                  </div>

                  <motion.button
                    onClick={handleResetGame}
                    className="w-full flex items-center gap-4 p-4 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <div className="p-2 bg-red-500 rounded-lg">
                      <RotateCcw size={20} />
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-semibold">Reset Current Game</p>
                      <p className="text-red-100 text-sm">Start over with new word positions</p>
                    </div>
                  </motion.button>
                </motion.div>
              )}

              {/* Bottom spacer for mobile scrolling */}
              <div className="h-16" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;